import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  TipoOrdenTrabajo,
  TipoOrdenTrabajoPagingRes,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchTipoOrdenTrabajo = ({
  page = 1,
  nombre = '',
  page_size = 10,
}: GetTiposOrdenTrabajoParams) => {
  return useQuery({
    queryKey: ['tiposTrabajo', page, nombre],
    queryFn: async () => {
      return getTiposOrdenTrabajo({ page, nombre, page_size });
    },
  });
};

export const useGetTipoOrdenTrabajo = (id: string) => {
  return useQuery({
    queryKey: ['tiposTrabajo', id],
    queryFn: async () => {
      return getTipoOrdenTrabajo(id);
    },
  });
};

export const useCreateTipoOrdenTrabajo = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTipoOrdenTrabajo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiposTrabajo'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useUpdateTipoOrdenTrabajo = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTipoOrdenTrabajo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiposTrabajo'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useRemoveTipoOrdenTrabajo = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeTipoOrdenTrabajo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiposTrabajo'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type GetTiposOrdenTrabajoParams = {
  page?: number;
  nombre?: string;
  page_size?: number;
};

export type CreateTipoOrdenTrabajoParams = Omit<
  TipoOrdenTrabajo,
  'id_tipo_orden_trabajo'
>;

export type UpdateTipoOrdenTrabajoParams = {
  id: string;
  data: CreateTipoOrdenTrabajoParams;
};

export const getTiposOrdenTrabajo = async (
  params?: GetTiposOrdenTrabajoParams
) => {
  const { page = 1, nombre = '', page_size = 10 } = params || {};

  return get<TipoOrdenTrabajoPagingRes>(
    `${urlBackend}api/tipos-ordenes-trabajo/?page=${page}&nombre=${nombre}&page_size=${page_size}`,
    true
  );
};

export const getTipoOrdenTrabajo = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<TipoOrdenTrabajo>(
    `${urlBackend}api/tipos-ordenes-trabajo/${id}/`,
    true
  );

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createTipoOrdenTrabajo = async (
  data: CreateTipoOrdenTrabajoParams
) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<TipoOrdenTrabajo>(
    `${urlBackend}api/tipos-ordenes-trabajo/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateTipoOrdenTrabajo = async ({
  data,
  id,
}: UpdateTipoOrdenTrabajoParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<TipoOrdenTrabajo>(
    `${urlBackend}api/v1/tipos-ordenes-trabajo/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const removeTipoOrdenTrabajo = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<TipoOrdenTrabajo>(
    `${urlBackend}api/v1/tipos-ordenes-trabajo/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};